<h1 mat-dialog-title class="mb-0">
  {{ 'properties.documentsPage.conductorManagement.title' | translate }}
</h1>
<p class="dialog-subtitle" *ngIf="propertyName">{{ propertyName }}</p>

<mat-dialog-content>
  <!-- Blocking context error: nothing can be retried from here -->
  <div class="alert alert-danger" *ngIf="contextError">
    <mat-icon class="alert-icon" fontSet="fal" fontIcon="fa-exclamation"></mat-icon>
    {{ contextError }}
  </div>

  <div class="alert alert-info" *ngIf="!contextError">
    <mat-icon class="alert-icon" fontSet="fal" fontIcon="fa-info-circle"></mat-icon>
    {{ 'properties.documentsPage.conductorManagement.hint' | translate }}
  </div>

  <!-- BEGIN - Create -->
  <div class="row align-items-start create-block">
    <div class="col-12 col-md-8">
      <mat-form-field class="w-100">
        <input
          matInput
          type="text"
          [formControl]="createControl"
          [attr.maxlength]="maxLength"
          [placeholder]="'properties.documentsPage.conductorManagement.fields.newName' | translate"
          [attr.aria-label]="'properties.documentsPage.conductorManagement.fields.newName' | translate"
          (keyup.enter)="onCreateSubmit()">
        <mat-label>{{ 'properties.documentsPage.conductorManagement.fields.newName' | translate }}</mat-label>
        <mat-hint>{{ createNormalizedValue.length }}/{{ maxLength }}</mat-hint>
        <mat-error *ngIf="createControl.hasError('conductorEmpty')">
          {{ 'properties.documentsPage.conductorManagement.errors.emptyName' | translate }}
        </mat-error>
        <mat-error *ngIf="createControl.hasError('conductorTooLong')">
          {{ 'properties.documentsPage.conductorManagement.errors.tooLong' | translate: { max: maxLength } }}
        </mat-error>
        <mat-error *ngIf="createControl.hasError('conductorDuplicate')">
          {{ 'properties.documentsPage.conductorManagement.errors.duplicate' | translate }}
        </mat-error>
      </mat-form-field>
    </div>
    <div class="col-12 col-md-4 text-md-right create-actions">
      <button
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="actionsDisabled"
        (click)="onCreateSubmit()">
        <mat-icon fontSet="fal" fontIcon="fa-plus"></mat-icon>
        {{ 'common.cta.create' | translate }}
      </button>
    </div>
    <div class="col-12" *ngIf="createServerError">
      <div class="alert alert-danger alert-compact">{{ createServerError }}</div>
    </div>
  </div>
  <!-- END - Create -->

  <mat-divider></mat-divider>

  <!-- Local filter, only useful on long lists -->
  <div class="row mt-3" *ngIf="rows.length > searchThreshold">
    <div class="col-12">
      <mat-form-field class="w-100">
        <input
          matInput
          type="text"
          [ngModel]="searchTerm"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="onSearchChange($event)"
          [placeholder]="'properties.documentsPage.conductorManagement.fields.search' | translate"
          [attr.aria-label]="'properties.documentsPage.conductorManagement.fields.search' | translate">
        <mat-icon matPrefix fontSet="fal" fontIcon="fa-search"></mat-icon>
      </mat-form-field>
    </div>
  </div>

  <!-- BEGIN - List -->
  <div class="conductor-list mt-2">
    <p class="empty-list" *ngIf="!rows.length">
      {{ 'properties.documentsPage.conductorManagement.emptyList' | translate }}
    </p>
    <p class="empty-list" *ngIf="rows.length && !displayedRows.length">
      {{ 'properties.documentsPage.conductorManagement.emptySearch' | translate }}
    </p>

    <div class="conductor-row" *ngFor="let row of displayedRows">
      <!-- Inline rename: it replaces the row controls, no nested dialog -->
      <div class="row align-items-center" *ngIf="activeRow === row && activeKind === 'editing'">
        <div class="col-12 col-md-7">
          <mat-form-field class="w-100">
            <input
              matInput
              #renameInput
              type="text"
              [ngModel]="draft"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onDraftChange($event)"
              [attr.maxlength]="maxLength"
              [attr.aria-label]="'properties.documentsPage.conductorManagement.fields.renameTo' | translate"
              (keyup.enter)="onSaveRename()">
            <mat-hint>{{ draftNormalized.length }}/{{ maxLength }}</mat-hint>
          </mat-form-field>
        </div>
        <div class="col-12 col-md-5 text-md-right row-actions">
          <button
            mat-raised-button
            color="primary"
            type="button"
            [disabled]="renameDisabled"
            (click)="onSaveRename()">
            <mat-icon fontSet="fal" fontIcon="fa-check"></mat-icon>
            {{ 'common.cta.save' | translate }}
          </button>
          <button mat-button type="button" [disabled]="!!savingRow" (click)="onCancelRowAction()">
            {{ 'common.cta.undo' | translate }}
          </button>
        </div>
        <div class="col-12" *ngIf="draftError">
          <div class="alert alert-danger alert-compact">{{ draftError }}</div>
        </div>
        <div class="col-12" *ngIf="renameImpact > 0">
          <div class="alert alert-warning alert-compact">
            {{ 'properties.documentsPage.conductorManagement.renameImpact' | translate: { count: renameImpact } }}
          </div>
        </div>
      </div>

      <!-- Idle row / inline deletion confirmation -->
      <div class="row align-items-center" *ngIf="!(activeRow === row && activeKind === 'editing')">
        <div class="col-12 col-md-6 conductor-name" [class.conductor-name-blank]="row.blank">
          {{ row.display }}
        </div>

        <div class="col-12 col-md-6 text-md-right row-actions"
          *ngIf="!(activeRow === row && activeKind === 'confirmingDelete')">
          <span class="docs-badge">
            <ng-container *ngIf="row.count === countUnknown">
              {{ 'properties.documentsPage.conductorManagement.docsCountUnknown' | translate }}
            </ng-container>
            <ng-container *ngIf="row.count === 0">
              {{ 'properties.documentsPage.conductorManagement.docsCountNone' | translate }}
            </ng-container>
            <ng-container *ngIf="row.count === 1">
              {{ 'properties.documentsPage.conductorManagement.docsCountOne' | translate }}
            </ng-container>
            <ng-container *ngIf="row.count > 1">
              {{ 'properties.documentsPage.conductorManagement.docsCountMany' | translate: { count: row.count } }}
            </ng-container>
          </span>

          <button
            mat-icon-button
            #renameTrigger
            type="button"
            color="primary"
            [disabled]="actionsDisabled"
            [matTooltip]="'properties.documentsPage.conductorManagement.actions.rename' | translate"
            [attr.aria-label]="'properties.documentsPage.conductorManagement.actions.rename' | translate"
            (click)="onStartRename(row)">
            <mat-icon fontSet="fal" fontIcon="fa-pencil"></mat-icon>
          </button>

          <button
            mat-icon-button
            type="button"
            color="warn"
            [disabled]="actionsDisabled || !canDelete(row)"
            [matTooltip]="(canDelete(row)
              ? 'properties.documentsPage.conductorManagement.actions.delete'
              : 'properties.documentsPage.conductorManagement.actions.deleteBlocked') | translate"
            [attr.aria-label]="'properties.documentsPage.conductorManagement.actions.delete' | translate"
            (click)="onStartDelete(row)">
            <mat-icon fontSet="fal" fontIcon="fa-trash"></mat-icon>
          </button>
        </div>

        <div class="col-12 col-md-6 text-md-right row-actions"
          *ngIf="activeRow === row && activeKind === 'confirmingDelete'">
          <span class="delete-question">
            {{ 'properties.documentsPage.conductorManagement.deleteQuestion' | translate }}
          </span>
          <button
            mat-raised-button
            color="warn"
            type="button"
            [disabled]="!!savingRow"
            (click)="onConfirmDelete()">
            <mat-icon fontSet="fal" fontIcon="fa-trash"></mat-icon>
            {{ 'properties.documentsPage.conductorManagement.actions.deleteConfirm' | translate }}
          </button>
          <button mat-button type="button" [disabled]="!!savingRow" (click)="onCancelRowAction()">
            {{ 'common.cta.undo' | translate }}
          </button>
        </div>
      </div>

      <!-- Per-row error: kept inline so the failing conductor stays identifiable -->
      <div class="row" *ngIf="row.error">
        <div class="col-12">
          <div class="alert alert-danger alert-compact">{{ row.error }}</div>
        </div>
      </div>
    </div>
  </div>
  <!-- END - List -->
</mat-dialog-content>

<div class="row custom-dialog-footer">
  <div class="col-12 text-right">
    <button mat-raised-button color="primary" type="button" [disabled]="!!savingRow || creating"
      (click)="attemptClose()">
      <mat-icon fontSet="fal" fontIcon="fa-times-circle"></mat-icon>
      {{ 'common.cta.close' | translate }}
    </button>
  </div> <!-- /.col -->
</div> <!-- /.row -->
